import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { 
  GraduationCap, 
  Heart, 
  FileText, 
  Users, 
  ArrowRight,
  CheckCircle2,
  BookOpen,
  IndianRupee,
  Download,
  ExternalLink
} from 'lucide-react';

const ScholarshipsPage = () => {
  const eligibilityCriteria = [
    'विद्यार्थी मारवाड़ी ब्रह्मक्षत्रिय समाज का होना चाहिए',
    'पिछली परीक्षा में न्यूनतम 60% अंक होने चाहिए',
    'परिवार की वार्षिक आय ₹5 लाख या उससे कम होनी चाहिए',
    'उसी शैक्षणिक सत्र के लिए समाज की किसी अन्य संस्था से छात्रवृत्ति प्राप्त नहीं होनी चाहिए',
    'एक परिवार से केवल एक विद्यार्थी को छात्रवृत्ति मिल सकती है'
  ];

  const requiredDocuments = [
    'पहचान प्रमाण (जैसे आधार कार्ड)',
    'पासपोर्ट साइज फोटो',
    'पिछली परीक्षा की अंकतालिका',
    'आय प्रमाण पत्र',
    'प्रवेश पत्र / संस्था में प्रवेश का प्रमाण',
    'पाठ्यक्रम का विवरण (Prospectus)',
    'संस्था के बैंक खाते का विवरण'
  ];

  const higherEducationIncluded = [
    'स्नातक पाठ्यक्रम (BA, BSc, B.Com, BBA, B.Tech, MBBS, आदि)',
    'स्नातकोत्तर पाठ्यक्रम (MA, MSc, M.Com, MBA, आदि)',
    'व्यावसायिक पाठ्यक्रम (CA, CS, CMA, LLB, Nursing, Pharmacy, आदि)',
    'मान्यता प्राप्त संस्थानों से न्यूनतम एक वर्षीय डिप्लोमा और प्रमाणन पाठ्यक्रम',
    'भारत या विदेश में उच्च शिक्षा'
  ];

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 bg-secondary/20 rounded-full mb-6">
            <GraduationCap className="w-10 h-10 text-secondary" />
          </div>
          <h1 className="font-serif font-bold text-4xl md:text-6xl text-primary mb-6 tracking-tight">
            Scholarships
          </h1>
          <h2 className="font-serif text-2xl md:text-3xl text-secondary mb-6">
            महासभा नीड कम मैरिट लोन स्कॉलरशिप
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            महासभा द्वारा छात्रवृत्ति वितरित की जाती रही है। वर्ष 2025 में छात्रवृत्ति नीति पुनर्लिखित व पुनः निर्धारित की गई।
          </p>
        </motion.div>

        {/* Introduction */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <Card className="p-8 md:p-12 bg-gradient-to-r from-primary/5 to-secondary/5 border-2 border-secondary/20">
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-foreground leading-relaxed mb-6">
                महासभा नीड कम मैरिट लोन स्कॉलरशिप पॉलिसी डॉक्युमेंट जारी किया गया और उसके अनुसार कोष प्रबंधन व छात्रवृत्ति वितरण का काम शुरू हो गया है।
              </p>
              <p className="text-lg text-foreground leading-relaxed mb-6">
                <strong className="text-primary">नयी नीति का मूल उद्देश्य है:</strong> समाज के होनहार व जरूरतमंद विद्यार्थियों को उच्च शिक्षा में योगदान देना व वे जब सफल हो जायें तब अन्य विद्यार्थियों के योगदान हेतु छात्रवृत्ति लौटाकर अपना कर्तव्य निभा सकें।
              </p>
              <p className="text-lg text-secondary font-semibold">
                स्वाभिमान युक्त यह प्रक्रिया सफल होने व दूसरों को सफल बनाने की योजना है।
              </p>
            </div>
          </Card>
        </motion.section>

        {/* CTA Cards */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Donor Registration Card */}
            <Card className="p-8 border-2 border-secondary hover:shadow-floating transition-shadow">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary/20 rounded-full mb-6">
                  <Heart className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="font-serif font-bold text-2xl text-primary mb-4">
                  शिक्षा मित्र बनें
                </h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  प्रारंभिक अवस्था में कोष प्रबंधन हेतु शिक्षा मित्रों से वार्षिक अंशदान लेने का प्रावधान है। यह अंशदान समाज जनों से आमंत्रित है।
                </p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-center gap-2 text-foreground">
                    <IndianRupee className="w-5 h-5 text-secondary" />
                    <span className="font-semibold">शिक्षा मित्र: ₹5,100/-</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-foreground">
                    <IndianRupee className="w-5 h-5 text-secondary" />
                    <span className="font-semibold">शिक्षा परममित्र: ₹11,000/-</span>
                  </div>
                </div>
                <Link to="/scholarship/donor-registration">
                  <Button className="bg-secondary hover:bg-secondary/90 text-white font-semibold px-8">
                    अंशदान करें <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </Card>

            {/* Student Application Card */}
            <Card className="p-8 border-2 border-primary hover:shadow-floating transition-shadow">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/20 rounded-full mb-6">
                  <BookOpen className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-serif font-bold text-2xl text-primary mb-4">
                  छात्रवृत्ति के लिए आवेदन करें
                </h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  नीति के अनुसार योग्य विद्यार्थियों से भी छात्रवृत्ति हेतु आवेदन आमंत्रित हैं। फॉर्म को पूरा भरें व वांछित प्रलेख संलग्न करें।
                </p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-center gap-2 text-foreground">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <span>न्यूनतम 60% अंक आवश्यक</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-foreground">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <span>वार्षिक आय ₹5 लाख या कम</span>
                  </div>
                </div>
                <Link to="/scholarship/application">
                  <Button className="bg-primary hover:bg-primary/90 text-white font-semibold px-8">
                    आवेदन करें <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        </motion.section>

        {/* Policy Document Download */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <Card className="p-8 bg-muted/50">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-primary/20 rounded-lg flex items-center justify-center">
                  <FileText className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h3 className="font-serif font-bold text-xl text-primary">छात्रवृत्ति पॉलिसी डॉक्युमेंट</h3>
                  <p className="text-muted-foreground">इस दस्तावेज़ का अध्ययन करें</p>
                </div>
              </div>
              <a 
                href="https://customer-assets.emergentagent.com/job_portal-pages/artifacts/qh6aze93_2025.06.30%20Scholarship%20Policy%20Document%202025.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="outline" className="font-semibold">
                  <Download className="mr-2 w-4 h-4" /> डाउनलोड करें
                </Button>
              </a>
            </div>
          </Card>
        </motion.section>

        {/* Eligibility Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="font-serif font-bold text-3xl text-primary mb-8 text-center">पात्रता मानदंड</h2>
          <Card className="p-8">
            <div className="space-y-4">
              {eligibilityCriteria.map((criteria, index) => (
                <div key={index} className="flex items-start gap-4 p-4 bg-muted/30 rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-foreground">{criteria}</span>
                </div>
              ))}
            </div>
          </Card>
        </motion.section>

        {/* Higher Education Definition */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="font-serif font-bold text-3xl text-primary mb-8 text-center">उच्च शिक्षा की परिभाषा</h2>
          <Card className="p-8 bg-gradient-to-r from-secondary/5 to-primary/5">
            <p className="text-lg text-muted-foreground mb-6">
              कक्षा 12 (उच्च माध्यमिक) पूर्ण करने के बाद UGC, AICTE, राज्य या विश्वविद्यालय द्वारा मान्यता प्राप्त डिप्लोमा, डिग्री या व्यावसायिक योग्यता प्राप्त करना।
            </p>
            <div className="space-y-3">
              {higherEducationIncluded.map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <GraduationCap className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground">{item}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 p-4 bg-destructive/10 rounded-lg border border-destructive/20">
              <p className="text-sm text-destructive font-medium">
                <strong>शामिल नहीं:</strong> स्कूली शिक्षा (कक्षा 1 से 12), गैर-शैक्षणिक या शौक पाठ्यक्रम (संगीत, चित्रकला आदि), एक वर्ष से कम अवधि के पाठ्यक्रम
              </p>
            </div>
          </Card>
        </motion.section>

        {/* Required Documents */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="font-serif font-bold text-3xl text-primary mb-8 text-center">आवश्यक दस्तावेज़</h2>
          <Card className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {requiredDocuments.map((doc, index) => (
                <div key={index} className="flex items-center gap-3 p-4 border rounded-lg">
                  <FileText className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-foreground">{doc}</span>
                </div>
              ))}
            </div>
          </Card>
        </motion.section>

        {/* Shiksha Mitra Details */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="font-serif font-bold text-3xl text-primary mb-8 text-center">शिक्षा मित्र / शिक्षा परममित्र</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="p-8 border-2 border-secondary/30 bg-secondary/5">
              <div className="text-center">
                <h3 className="font-serif font-bold text-2xl text-secondary mb-4">शिक्षा मित्र</h3>
                <div className="text-4xl font-bold text-foreground mb-4">₹5,100/-</div>
                <p className="text-muted-foreground mb-4">वार्षिक अंशदान</p>
                <div className="bg-white p-4 rounded-lg mb-4">
                  <p className="text-sm text-muted-foreground">लक्ष्य: <strong className="text-foreground">200 सदस्य</strong></p>
                </div>
                <p className="text-sm text-secondary font-medium">
                  "शिक्षा मित्र" की उपाधि से सम्मानित किया जाएगा
                </p>
              </div>
            </Card>

            <Card className="p-8 border-2 border-primary/30 bg-primary/5">
              <div className="text-center">
                <h3 className="font-serif font-bold text-2xl text-primary mb-4">शिक्षा परममित्र</h3>
                <div className="text-4xl font-bold text-foreground mb-4">₹11,000/-</div>
                <p className="text-muted-foreground mb-4">वार्षिक अंशदान</p>
                <div className="bg-white p-4 rounded-lg mb-4">
                  <p className="text-sm text-muted-foreground">लक्ष्य: <strong className="text-foreground">100 सदस्य</strong></p>
                </div>
                <p className="text-sm text-primary font-medium">
                  "शिक्षा परममित्र" की उपाधि से सम्मानित किया जाएगा
                </p>
              </div>
            </Card>
          </div>
        </motion.section>

        {/* Repayment Info */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <Card className="p-8 bg-gradient-to-r from-primary to-primary/80 text-white">
            <div className="text-center">
              <h2 className="font-serif font-bold text-3xl mb-6">छात्रवृत्ति की प्रकृति</h2>
              <p className="text-lg opacity-90 mb-6 max-w-3xl mx-auto">
                यह एक ब्याज-मुक्त ऋण जैसी वित्तीय सहायता है। प्राप्तकर्ताओं से अपेक्षा है कि वे रोजगार प्राप्त करने के बाद इसे वापस करें।
              </p>
              <div className="inline-block bg-white/20 rounded-lg px-6 py-4">
                <p className="font-semibold">
                  पुनर्भुगतान: रोजगार प्राप्त करने के बाद 24 मासिक किश्तों में या एकमुश्त
                </p>
              </div>
            </div>
          </Card>
        </motion.section>

        {/* Call to Action */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h2 className="font-serif font-bold text-3xl text-primary mb-6">
            आपसी सहयोग की भावना से आगे बढ़ने की इस योजना को सफल बनाने का आह्वान है
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/scholarship/donor-registration">
              <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-white font-semibold px-8">
                <Heart className="mr-2 w-5 h-5" /> शिक्षा मित्र बनें
              </Button>
            </Link>
            <Link to="/scholarship/application">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-white font-semibold px-8">
                <GraduationCap className="mr-2 w-5 h-5" /> छात्रवृत्ति के लिए आवेदन करें
              </Button>
            </Link>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default ScholarshipsPage;
